import {
  useWSModal,
  WSButton,
  WSButtons,
  WSElement,
  WSElementProps,
  WSForm,
  WSInputMask,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesComplianceEntities } from "@query";
import { getSsnInputProps } from "../../../../shared/utils/getSsnInputProps";
import { validatorTaxId } from "../../../../shared/validators/validatorTaxId";
import { WS_LINKS } from "../../../../types/wsLinks";

type FormData = {
  ssn: string;
};

type Props = {
  complianceEntityId: string;
  onClose: (result: boolean) => void;
} & WSElementProps;

const ModalAddSSN: React.FC<Props> = props => {
  const { complianceEntityId, onClose, ...elementProps } = props;
  const [updateComplianceEntity, updateComplianceEntityMeta] =
    queriesComplianceEntities.updateComplianceEntity.useMutation();

  return (
    <WSElement {...elementProps}>
      <WSText.Heading5 mb="M">
        Add social security number or ITIN to send payment.
      </WSText.Heading5>
      <WSText my="XL">
        The IRS requires your full SSN or ITIN to receive your payment.
      </WSText>

      <WSForm<FormData>
        defaultValues={{ ssn: "" }}
        validationSchema={Yup.object().shape({
          ssn: validatorTaxId("ssn", "Individual", "US").required("Required")
        })}
        onSubmit={async formData => {
          await updateComplianceEntity(
            {
              complianceEntityId,
              complianceEntityUpdate: {
                taxIdentifiers: [
                  { countryCode: "US", type: "SSN", value: formData.ssn }
                ]
              }
            },
            { onSuccess: () => onClose(true) }
          );
        }}
      >
        <WSForm.Field
          label="SSN or ITIN"
          name="ssn"
          component={WSInputMask}
          componentProps={getSsnInputProps()}
          mb="2XL"
        />

        <ErrorAlert
          context="MonitoredEntity"
          error={updateComplianceEntityMeta.error}
          mb="2XL"
        />

        <WSButtons format="modal">
          <WSButton.Secondary
            size="M"
            type="button"
            onClick={() => onClose(false)}
          >
            Cancel
          </WSButton.Secondary>
          <WSButton.Primary
            size="M"
            type="submit"
            loading={updateComplianceEntityMeta.isLoading}
          >
            Submit SSN or ITIN
          </WSButton.Primary>
        </WSButtons>
      </WSForm>

      <WSText.ParagraphSm mt="XL">
        Wingspan uses bank-grade security to protect your data.{" "}
        <a href={WS_LINKS.security} target="_blank" rel="noreferrer">
          Learn more.
        </a>
      </WSText.ParagraphSm>
    </WSElement>
  );
};

export const useModalAddSSN = () =>
  useWSModal(ModalAddSSN, {
    title: "Add SSN or ITIN",
    size: "S"
  });
